// Free, keyless lead sources queried straight from the browser (they allow cross-origin calls):
// Wikidata organisations: head offices with their own website, headcount and LinkedIn page, by kind and country.
// The engines that need keys and a daily cap stay in the lead_search edge function.
import type { Lead, LeadFilters } from "./leadFilters.ts";

export const OPEN_ENGINES = ["wikidata_orgs"] as const;
export type OpenEngine = (typeof OPEN_ENGINES)[number];
export const isOpenEngine = (engine: string): engine is OpenEngine =>
  (OPEN_ENGINES as readonly string[]).includes(engine);

const WIKIDATA = "https://query.wikidata.org/sparql";
export const WIKIDATA_LIMIT = 40;

/** Kinds of organisation by the words a segment uses; narrow classes take subclasses, broad ones only direct members */
const KINDS: { words: RegExp; classes: string[]; deep: boolean }[] = [
  { words: /\b(bank|banque|banking)/i, classes: ["Q22687"], deep: true },
  {
    words: /\b(insurance|insurer|takaful)/i,
    classes: ["Q2143354"],
    deep: true,
  },
  {
    words: /\b(hospital|clinic|medical|health)/i,
    classes: ["Q16917"],
    deep: true,
  },
  {
    words: /\b(ministry|ministries|minist)/i,
    classes: ["Q192350"],
    deep: true,
  },
  {
    words:
      /\b(government|authority|authorities|agency|regulator|public sector|municipal)/i,
    classes: ["Q327333"],
    deep: false,
  },
  {
    words: /\b(telecom|mobile network|operator)/i,
    classes: ["Q2401749"],
    deep: true,
  },
  {
    words: /\b(manufactur|factory|factories|industrial|vehicle|automotive)/i,
    classes: ["Q13235160"],
    deep: true,
  },
  {
    words: /\b(universit|college|school|education)/i,
    classes: ["Q3918"],
    deep: true,
  },
];

/** Wikidata items of the countries a segment names (its locations are "City, Country" or a country) */
const COUNTRIES: [RegExp, string, string][] = [
  [/egypt/i, "Q79", "EG"],
  [/emirates|uae|dubai|abu dhabi/i, "Q878", "AE"],
  [/saudi|ksa|riyadh|jeddah/i, "Q851", "SA"],
  [/qatar|doha/i, "Q846", "QA"],
  [/kuwait/i, "Q817", "KW"],
  [/bahrain|manama/i, "Q398", "BH"],
  [/oman|muscat/i, "Q842", "OM"],
  [/jordan|amman/i, "Q810", "JO"],
  [/morocco/i, "Q1028", "MA"],
];

/** What to ask Wikidata for a segment: the kinds its words name and the countries of its locations; null when none */
export const wikidataPlan = (filters: LeadFilters) => {
  const words = [...filters.keywords, ...filters.job_titles].join(" ");
  const kinds = KINDS.filter((k) => k.words.test(words));
  const countries = COUNTRIES.filter(([re]) =>
    filters.locations.some((l) => re.test(l)),
  );
  if (!kinds.length || !countries.length) return null;
  return { kinds, countries };
};

export const wikidataQuery = (
  plan: NonNullable<ReturnType<typeof wikidataPlan>>,
) => {
  const countries = plan.countries.map(([, q]) => `wd:${q}`).join(" ");
  const kinds = plan.kinds
    .map((k) =>
      k.classes
        .map((c) => `{ ?org wdt:P31${k.deep ? "/wdt:P279*" : ""} wd:${c}. }`)
        .join(" UNION "),
    )
    .join(" UNION ");
  return `SELECT ?org ?orgLabel ?country (SAMPLE(?w) AS ?website) (MAX(?e) AS ?employees) (SAMPLE(?l) AS ?li) (SAMPLE(?hqL) AS ?hq) (SAMPLE(?indL) AS ?industry) WHERE {
  VALUES ?country { ${countries} }
  ?org wdt:P17 ?country; wdt:P856 ?w.
  ${kinds}
  FILTER NOT EXISTS { ?org wdt:P576 ?end }
  OPTIONAL { ?org wdt:P1128 ?e }
  OPTIONAL { ?org wdt:P4264 ?l }
  OPTIONAL { ?org wdt:P159 ?hqItem. ?hqItem rdfs:label ?hqL. FILTER(LANG(?hqL) = "en") }
  OPTIONAL { ?org wdt:P452 ?ind. ?ind rdfs:label ?indL. FILTER(LANG(?indL) = "en") }
  SERVICE wikibase:label { bd:serviceParam wikibase:language "en,ar". }
} GROUP BY ?org ?orgLabel ?country LIMIT ${WIKIDATA_LIMIT}`;
};

const value = (row: any, key: string): string | undefined => {
  const v = row?.[key]?.value;
  return typeof v === "string" && v.trim() ? v.trim() : undefined;
};

const domainOfUrl = (url?: string) => {
  try {
    return url ? new URL(url).hostname.replace(/^www\./, "") : undefined;
  } catch {
    return undefined;
  }
};

/** Wikidata rows as leads: one per organisation, with its kind as the industry when Wikidata names none */
export const wikidataLeads = (
  rows: any[],
  plan: NonNullable<ReturnType<typeof wikidataPlan>>,
): Lead[] =>
  rows.flatMap((row): Lead[] => {
    const id = value(row, "org")?.split("/").pop();
    const name = value(row, "orgLabel");
    // an unlabelled item comes back as its own id (Q123): not a usable name
    if (!id || !name || /^Q\d+$/.test(name)) return [];
    const website = value(row, "website");
    const country = plan.countries.find(([, q]) =>
      value(row, "country")?.endsWith(`/${q}`),
    )?.[2];
    const employees = Number(value(row, "employees"));
    const li = value(row, "li");
    return [
      {
        ref: `wikidata:${id}`,
        source: "wikidata",
        company: {
          name,
          website,
          domain: domainOfUrl(website),
          city: value(row, "hq"),
          country,
          industry: value(row, "industry") ?? kindName(plan),
          employees:
            Number.isFinite(employees) && employees > 0 ? employees : undefined,
          linkedin_url: li
            ? `https://www.linkedin.com/company/${li}`
            : undefined,
        },
      },
    ];
  });

const KIND_NAMES: Record<string, string> = {
  Q22687: "Bank",
  Q2143354: "Insurance company",
  Q16917: "Hospital",
  Q192350: "Ministry",
  Q327333: "Government agency",
  Q2401749: "Telecommunications company",
  Q13235160: "Manufacturer",
  Q3918: "University",
};
const kindName = (plan: NonNullable<ReturnType<typeof wikidataPlan>>) =>
  plan.kinds.map((k) => KIND_NAMES[k.classes[0]]).join(" / ");

/** Organisations for a segment from Wikidata (free, no key); [] when the segment names no kind or country it knows */
export const wikidataSearch = async (
  filters: LeadFilters,
  fetchFn: typeof fetch = fetch,
): Promise<Lead[]> => {
  const plan = wikidataPlan(filters);
  if (!plan) return [];
  const response = await fetchFn(
    `${WIKIDATA}?query=${encodeURIComponent(wikidataQuery(plan))}`,
    { headers: { Accept: "application/sparql-results+json" } },
  );
  if (!response.ok) throw new Error(`Wikidata answered ${response.status}`);
  const body = (await response.json()) as {
    results?: { bindings?: any[] };
  };
  return wikidataLeads(body.results?.bindings ?? [], plan);
};
